// Phase 81vv — PickLeague homepage VĐV (mobile-first dark theme)
// Phase 82h — Wire HeroBanner + UpcomingTournaments + LiveMatches sections to real APIs
// Sprint 2 (02-2026) — Lazy-load 4 component below-the-fold (FeaturedStream,
// QuickRanking, TopPlayerCarousel, LatestHighlights). Above-fold vẫn eager
// (Header + QuickStats + HeroBanner + LiveMatches + UpcomingTournaments) để
// LCP không bị Suspense fallback. Ước tính cắt ~150-250KB initial JS.
import { asset } from "../lib/asset";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useGuestGate } from "../contexts/GuestLoginGateContext";
import { useRatingGate } from "../contexts/RatingGateContext";
import { useAvatarGate } from "../contexts/AvatarGateContext";
import Header from "../components/layout/Header";
import QuickStats from "../components/home/QuickStats";
import HeroBanner from "../components/home/HeroBanner";
import LiveMatches from "../components/home/LiveMatches";
import UpcomingTournaments from "../components/home/UpcomingTournaments";
import { lazy, Suspense, useEffect, useState } from "react";
// Sprint 2 (02-2026) — FeaturedStream/QuickRanking từng import nhưng chưa
// render trên trang chủ VĐV — xóa để không bloat main bundle.
const TopPlayerCarousel = lazy(() => import("../components/home/TopPlayerCarousel"));
const LatestHighlights = lazy(() => import("../components/home/LatestHighlights"));
const HighlightPlayerModal = lazy(() => import("../components/athlete/profile/HighlightPlayerModal"));
import BottomNav from "../components/layout/BottomNav";
import { liveMatchPath } from "../lib/liveMatchPath";
// Phase 83g — Removed FloatingActionButton import (replaced by simple BottomNav)
// Sprint 2 (02-2026): Bỏ import mockStreams/mockRanking/mockHighlights
// vì đã dùng real API + component tương ứng cũng đã lazy/loại bỏ.
import useHomeData from "../hooks/useHomeData";
import { api } from "../lib/api";
import { edgeGet } from "../lib/edgeApi";
import { directThumbUrl } from "../lib/imgProxy";
import { registerMediaView } from "../lib/mediaViews";

export default function AthleteHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { requireAuth } = useGuestGate();
  const { requireRatingVerified } = useRatingGate();
  const { requireCustomAvatar } = useAvatarGate();
  // Phase 84.M.38 — Wrap navigate cho guest: nếu chưa login, mở GuestLoginGate
  // thay vì hard-redirect đến /login.
  const navOrGate = (path, hint) => requireAuth(() => navigate(path), { hint });
  // Phase 84.M.85 — Helper compose: auth gate → rating gate → navigate.
  // v16.6 — Chain thêm avatar gate: chưa upload ảnh thật → chặn.
  const registerOrGate = (path, hint) =>
    requireAuth(
      () => requireRatingVerified(() => requireCustomAvatar(() => navigate(path))),
      { hint },
    );
  const {
    heroTournaments,
    upcomingTournaments,
    liveMatches,
    homeStats,
    isHeroFallback,
    isLiveFallback,
    isFinalRecap,
    ranking,
    rankingScope,
    rankingMyExtra,
    myId,
  } = useHomeData();

  const onSeeAllToast = (label) => () => toast.info(`${label}: sắp ra mắt`);

  // Phase 85.A.115 — Fetch latest highlights real data + modal player.
  const [latestHighlights, setLatestHighlights] = useState([]);
  const [pickedHighlight, setPickedHighlight] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // v17.86 (Feb 2026) — Tối đa 8 video ở trang chủ, merge cả VOD của
        // trận CK/BK/TK gần nhất (backend đã trộn theo created_at desc).
        const r = await edgeGet("/public/highlights/latest", { params: { limit: 8 } });
        if (cancelled) return;
        // Map shape API → format LatestHighlights component expects.
        const mapped = (r?.data?.items || []).map((v) => ({
          ...v, // giữ full fields cho modal (source, hls_url, playback_url...)
          thumbnail: directThumbUrl(v) || asset("/images/no-thumb.png"),
          duration: v.duration_seconds
            ? `${Math.floor(v.duration_seconds / 60)}:${String(Math.floor(v.duration_seconds % 60)).padStart(2, "0")}`
            : "—",
          views: typeof v.view_count === "number"
            ? (v.view_count > 1000 ? `${(v.view_count / 1000).toFixed(1)}K` : String(v.view_count))
            : "0",
        }));
        setLatestHighlights(mapped);
      } catch {
        if (!cancelled) setLatestHighlights([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Phase 83z.12 + 83z.16 + 84.M.22 — Build real stats OBJECT (shape khớp với
  // QuickStats: keys = points/rank/winrate/tournaments).
  // 84.M.22 — `rank` now ships as a STRING "Top N/Total" (vd "Top 15/300") and
  // a separate `rankLabel` (province name) so the card shows the province
  // under the rank value. QuickStats handles string values for `rank`
  // gracefully (skips the locale-format path when raw is non-number).
  const realStatsObj = (() => {
    const s = homeStats || {};
    const out = {};
    if (s.rating != null) out.points = Number(s.rating).toFixed(3);
    if (s.region_rank != null) {
      const total = Number(s.region_rank_total) || 0;
      out.rank = total > 0 ? `Top ${s.region_rank}/${total}` : `Top ${s.region_rank}`;
      out.rankLabel = s.region_province_name || s.region_label || "Khu vực";
    }
    if (s.winrate != null) out.winrate = s.winrate;
    if (s.tournaments_played != null) out.tournaments = s.tournaments_played;
    return out;
  })();
  const realStatsHasAny = Object.keys(realStatsObj).length > 0;

  return (
    <div className="min-h-screen bg-[#060B14] text-white" data-testid="athlete-home-page">
      <BackgroundDecor />
      <div className="relative pb-[110px]">
        <Header user={user} />
        {/* Phase 85.A.119d — Bỏ banner auto-nhắc cập nhật avatar (Phase 85.A.69).
            VĐV có thể cập nhật avatar bất cứ lúc nào ở trang Profile. Modal bắt
            buộc chỉ trigger khi admin chủ động đề xuất qua AvatarProposalGate. */}
        {/* Phase 84.M.40 — Bỏ badge "MOCK" + ẩn hẳn section khi không có data
            thật thay vì hiển thị giải mock fake. */}
        {realStatsHasAny && <QuickStats stats={realStatsObj} />}
        {heroTournaments.length > 0 && (
          <HeroBanner
            tournaments={heroTournaments}
            onView={(t) => navigate(`/athlete/tournaments/${t.slug || t.id}`)}
            onJoin={(t) => registerOrGate(`/athlete/tournaments/${t.slug || t.id}/register`, "Đăng nhập để đăng ký giải đấu này.")}
          />
        )}
        {/* Phase 90.C.home-cleanup — Removed "LIVE ĐẶC BIỆT" hero ticker
            (HeroLiveTicker) per user request — no longer used. */}
        {liveMatches.length > 0 && (
          <LiveMatches
            matches={liveMatches}
            isRecap={isFinalRecap}
            onSeeAll={() => navOrGate("/athlete/matches", "Đăng nhập để xem tất cả trận đấu của bạn.")}
            onWatch={(m) => navigate(liveMatchPath(m))}
          />
        )}
        {upcomingTournaments.length > 0 ? (
          <UpcomingTournaments
            tournaments={upcomingTournaments}
            onSeeAll={() => navigate("/athlete/tournaments")}
            onSelect={(t) => navigate(`/athlete/tournaments/${t.id}`)}
          />
        ) : (
          <HomeEmptyState />
        )}
        {/* Phase 85.A.115 — Re-enabled LatestHighlights với real API data.
            Sprint 2 (02-2026): 4 component dưới lazy-load, Suspense fallback
            là khoảng trống mỏng để không chiếm CLS. */}
        <Suspense fallback={<div className="min-h-[120px]" />}>
          <LatestHighlights
            items={latestHighlights}
            onSeeAll={() => navigate("/athlete/matches?tab=highlights")}
            onPlay={(v) => {
              // Phase 85.A.358 — Mỗi click xem = +1 lượt xem (đồng bộ mọi loại).
              registerMediaView(v);
              setLatestHighlights((prev) =>
                prev.map((x) => {
                  if (x.id !== v.id) return x;
                  const nextCount = (Number(x.view_count) || 0) + 1;
                  return {
                    ...x,
                    view_count: nextCount,
                    views: nextCount > 1000 ? `${(nextCount / 1000).toFixed(1)}K` : String(nextCount),
                  };
                }),
              );
              setPickedHighlight(v);
            }}
          />
          <TopPlayerCarousel
            onSeeAll={() => navigate("/athlete/ranking")}
            onSelect={(p) => navigate(`/athlete/profile/${p.username || p.id}`)}
          />
        </Suspense>
      </div>
      <Suspense fallback={null}>
        <HighlightPlayerModal
          open={!!pickedHighlight}
          video={pickedHighlight}
          onClose={() => setPickedHighlight(null)}
        />
      </Suspense>
      <BottomNav />
    </div>
  );
}

function HomeEmptyState() {
  return (
    <section className="px-5 pt-8 pb-2" data-testid="home-empty-state">
      <div className="rounded-[24px] border border-dashed border-white/10 bg-white/[0.02] p-8 text-center">
        <div className="text-4xl mb-3">🎾</div>
        <h3 className="text-[15px] font-black text-white mb-1.5">
          Chưa có giải đấu sắp diễn ra
        </h3>
        <p className="text-[12.5px] text-white/55 leading-relaxed max-w-xs mx-auto">
          Các giải mới sẽ xuất hiện tại đây ngay khi BTC mở đăng ký. Hãy theo dõi để không bỏ lỡ!
        </p>
      </div>
    </section>
  );
}

function BackgroundDecor() {
  return (
    <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
      <div
        className="absolute -top-40 -left-40 w-[420px] h-[420px] rounded-full opacity-30"
        style={{
          background: "radial-gradient(circle, rgba(255,138,0,0.18) 0%, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full opacity-25"
        style={{
          background: "radial-gradient(circle, rgba(168,255,0,0.16) 0%, transparent 70%)",
        }}
      />
    </div>
  );
}
